import React, { useEffect, useState } from 'react';
import './InstallPrompt.css';

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

/* Saffron-lambda mark — matches the login wordmark + sidebar brand. */
function Mark() {
  return (
    <svg className="ip-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3 L11 21 L13 21 L21 3 L17 3 L12 14 L7 3 Z" fill="#D4A537"/>
    </svg>
  );
}

/* Customer-facing portal routes — staff/owner aren't using these so the
   "Install Vyapar Pro" banner shouldn't appear there. */
const isCustomerPortal = () => {
  const p = window.location.pathname;
  return p.startsWith('/c/') || p.startsWith('/m/');
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOS, setShowIOS]               = useState(false);
  const [visible, setVisible]               = useState(false);
  const [dismissed, setDismissed]           = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (sessionStorage.getItem('install_dismissed')) return;
    if (isCustomerPortal()) return;

    // Android / Chrome — listen for the browser's install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — show manual instructions after a delay
    if (isIOS()) {
      setTimeout(() => setShowIOS(true), 3000);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem('install_dismissed', '1');
  };

  if (!visible || dismissed) return null;

  // ── iOS banner (manual instructions) ─────────────────
  if (showIOS && !deferredPrompt) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Install app">
        <Mark />
        <div className="ip-text">
          <p className="ip-title">Install Vyapar Pro</p>
          <p className="ip-sub">
            Tap <strong>Share</strong> ⎙ then <strong>Add to Home Screen</strong>
          </p>
        </div>
        <button className="ip-close" onClick={handleDismiss} aria-label="Dismiss">✕</button>
      </div>
    );
  }

  // ── Android / desktop banner (one-tap install) ───────
  if (deferredPrompt) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Install app">
        <Mark />
        <div className="ip-text">
          <p className="ip-title">Install Vyapar Pro</p>
          <p className="ip-sub">Add to home screen for quick access</p>
        </div>
        <button className="ip-install" onClick={handleInstall}>Install</button>
        <button className="ip-close" onClick={handleDismiss} aria-label="Dismiss">✕</button>
      </div>
    );
  }

  return null;
}
